import {
  BanknotesIcon,
  Breadcrumb,
  Button,
  CalendarDaysIcon,
  DownloadExcel,
  ErrorAlert,
  formatDate,
  initFlowbite,
  Input,
  MagnifyingGlassIcon,
  PageHero,
  PrinterIcon,
  useCharity,
  useFetchData,
  useState,
} from "@";
import Skeleton from "./components/Skeleton";
import { getFinancialReport } from "./utils/api";
import TabsHeader from "./components/TabsHeader";
import TabsContent from "./components/TabsContent";
import { exportToExcelSingleSheet } from "./utils/helpers";

const Index = () => {
  const [search, setSearch] = useState({
    fromDate: new Date(new Date().setHours(0, 0, 0, 0)),
    toDate: new Date(new Date().setHours(23, 59, 59, 999)),
  });

  const links = [
    { name: "التبرعات", link: "/donations" },
    { name: "القارير", link: "" },
  ];

  const { charityLogo, charityName } = useCharity();

  const { data, error, fetchData, isLoading } = useFetchData({
    noPgn: true,
    params: [search],
    fun: getFinancialReport,
  });

  if (!isLoading) {
    initFlowbite();
  }

  const handleInputChange = (field, value) => {
    setSearch((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <ErrorAlert reload={fetchData} show={error} />
      <Breadcrumb links={links} />

      {/* Print-only header */}
      <div className="mb-8 hidden print:flex print:items-center">
        <img
          src={charityLogo}
          alt={charityName}
          className="h-20 w-20 rounded-md bg-white object-cover"
        />
        <span className="ms-5 text-2xl">{charityName}</span>
      </div>

      <main className="table-card relative pb-5">
        {/* Screen-only refined header */}
        <div className="print:hidden">
          <PageHero
            eyebrow="التقارير / المالي"
            title="التقرير المالي"
            icon={BanknotesIcon}
          />
        </div>

        {/* Print-only header (fallback) */}
        <header className="hidden print:block print:text-center">
          <h2 className="page-title mb-2">{charityName}</h2>
          <h1 className="page-title">التقرير المالي</h1>
          <h3 className="page-title">
            <span className="inline-block">الفترة من</span>
            <span className="mx-2 inline-block">
              {search?.fromDate ? formatDate(search?.fromDate) : "..."}
            </span>
            الي
            <span className="mx-2 inline-block">
              {search?.toDate ? formatDate(search?.toDate) : "..."}
            </span>
          </h3>
          <h3 className="page-title">
            <span className="inline-block">تاريخ الاصدار</span>
            <span className="mx-2 inline-block">{formatDate(new Date())}</span>
          </h3>
        </header>

        {/* Date range toolbar */}
        <div className="relative mt-4 flex flex-wrap items-end gap-3 rounded-2xl border border-gray-100 bg-gradient-to-l from-purple-50/30 via-white to-white p-3 dark:border-gray-700/60 dark:from-purple-950/15 dark:via-gray-800 dark:to-gray-800 print:hidden">
          <div className="flex items-center gap-2 self-start pt-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-50 text-purple-700 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40">
              <CalendarDaysIcon className="h-5 w-5" />
            </span>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-purple-700 dark:text-purple-400">
                الفترة الزمنية
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                اختر النطاق ثم اضغط بحث
              </p>
            </div>
          </div>

          <div className="flex-1">
            <Input
              star
              showTimeSelect
              type="date-range"
              placeholderA="من تاريخ"
              placeholderB="الي تاريخ"
              valueB={search.toDate}
              valueA={search.fromDate}
              changeA={(e) => handleInputChange("fromDate", e)}
              changeB={(e) => handleInputChange("toDate", e)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 self-end">
            <Button onClick={fetchData}>
              <MagnifyingGlassIcon className="h-5 w-5" />
              بحث
            </Button>

            <DownloadExcel
              length={data?.length}
              click={() =>
                exportToExcelSingleSheet(
                  data,
                  `${charityName}-من-تاريخ-${formatDate(search.fromDate) || ""}-إلى-تاريخ-${formatDate(search.toDate) || ""}.xlsx`,
                  `من تاريخ ${formatDate(search.fromDate) || ""} إلى تاريخ ${formatDate(search.toDate) || ""}`,
                )
              }
            />

            <Button
              onClick={() => window.print()}
              className="btn-outline-green inline-flex items-center gap-2"
            >
              <PrinterIcon className="h-5 w-5" />
              طباعة
            </Button>
          </div>
        </div>

        <div className="relative mt-6">
          {isLoading && <Skeleton />}
          <TabsHeader />
          <TabsContent data={data} />
        </div>
      </main>
    </>
  );
};

export default Index;
